import React, { useState } from 'react';
import ValidatorUtils from 'services/ValidatorUtils';
import './style.scss';
import { api } from 'careand-redux/api/ApiThunks';
import { useAppDispatch } from 'careand-redux/useAppDispatch';
import DefaultButton from 'components/Utils/NewDefaultButton';
import { useIsLoading, useNavStack } from 'careand-redux/slices/ui/hooks';
import { Stacks } from 'careand-redux/slices/ui/navigation';
import TopBar from 'scenes/HomePatient2/TopBar';
import DefaultPatientTextField from 'components/Utils/DefaultPatientTextField';
import logo from 'assets/img/logo-full-blue.webp';
import EntryView from 'scenes/HomePatient2/PatientEntry/view';
import { UserRoleEnum } from 'generated/api';
import { getTenantIdentity } from 'hooks/useTenantIdentity';

const ForgotPassword = () => {
  const dispatch = useAppDispatch();
  const nav = useNavStack(Stacks.Master);

  const [sentCode, setSentCode] = useState(false);
  const [username, setUsername] = useState('');

  const sendingConfirmation = useIsLoading(api.Authentication.recoverPassword);
  const settingPassword = useIsLoading(api.Authentication.verifyPasswordRecovery);

  function onSetPasswordSuccess() {
    nav.push(`/login?username=${username}`);
  }

  async function sendPasswordConfirmation(event, resend = false) {
    if (event) {
      event.preventDefault();
    }
    const { isSuccess } = await dispatch(
      api.Authentication.newRecoverPassword({
        username,
        resend,
      }),
      { errorTitle: 'Password Recovery', meta: true },
    );
    if (isSuccess) {
      setSentCode(true);
    }
  }

  async function onChangePassword(event, values) {
    event.preventDefault();
    let { password, code } = values;

    const { isSuccess } = await dispatch(
      api.Authentication.newVerifyPasswordRecovery({
        username,
        passwordVerification: { password, code },
      }),
      {
        title: 'Password Recovery',
        successText: 'Your password has been changed successfully!',
        meta: true,
      },
    );
    if (isSuccess) {
      onSetPasswordSuccess();
    }
  }

  return (
    <EntryView className="forgot-password" topContent={<TopBar title="Forgot Password" />}>
      <div className="desktop-only">
        <div className="top-content">
          <img src={logo} alt={getTenantIdentity().name} className="logo" />
          <span className="top-text large desktop-only">Forgot Password</span>
        </div>
      </div>
      {!sentCode && (
        <UsernameForm
          username={username}
          setUsername={setUsername}
          sendingConfirmation={sendingConfirmation}
          onSubmit={sendPasswordConfirmation}
        />
      )}
      {sentCode && (
        <ConfirmCodeForm
          onChangePassword={onChangePassword}
          settingPassword={settingPassword}
          sendingConfirmation={sendingConfirmation}
          onResendCode={(event) => sendPasswordConfirmation(event, true)}
          username={username}
        />
      )}
    </EntryView>
  );
};

const UsernameForm = ({ onSubmit, username, setUsername, sendingConfirmation }) => {
  let error = ValidatorUtils.validateUsername(username);

  return (
    <form className="forgot-password-form" onSubmit={onSubmit}>
      <DefaultPatientTextField
        name="username"
        label="Username"
        value={username}
        onChange={(event) => setUsername(event.target.value)}
        error={error}
      />

      <DefaultButton
        id="forgot-password-send-confirmation"
        name="Send Confirmation Code"
        style={{ width: '100%', marginTop: '27px' }}
        loading={sendingConfirmation}
        disabled={error}
      />
    </form>
  );
};

const ConfirmCodeForm = ({
  onChangePassword,
  settingPassword,
  sendingConfirmation,
  onResendCode,
  username,
}) => {
  const [values, setValues] = useState({
    code: '',
    password: '',
    passwordConfirmation: '',
  });

  function onChange(event) {
    setValues((prevState) => {
      return {
        ...prevState,
        [event.target.name]: event.target.value,
      };
    });
  }

  let errors = {};

  errors.password = ValidatorUtils.validatePassword(values.password);
  errors.passwordConfirmation = ValidatorUtils.validatePassword(values.passwordConfirmation);

  if (!values.code) errors.code = 'Please type the confirmation code';
  if (values.password !== values.passwordConfirmation)
    errors.passwordConfirmation = 'Passwords do not match';

  return (
    <>
      <span className="top-text">A confirmation code has been sent to your phone.</span>
      <form id="change-password-form" onSubmit={(event) => onChangePassword(event, values)}>
        <DefaultPatientTextField
          name="username"
          label="Username"
          value={username}
          disabled={true}
          error={errors.username}
        />
        <DefaultPatientTextField
          name="password"
          label="New Password"
          type="password"
          value={values.password}
          onChange={onChange}
          error={errors.password}
        />
        <DefaultPatientTextField
          name="passwordConfirmation"
          value={values.passwordConfirmation}
          label="Confirm New Password"
          type="password"
          onChange={onChange}
          error={errors.passwordConfirmation}
        />
        <DefaultPatientTextField
          name="code"
          value={values.code}
          label="Confirmation Code"
          onChange={onChange}
          error={errors.code}
        />
        <DefaultButton
          id="change-password-submit"
          name="Change Password"
          style={{ width: '100%', marginTop: '20px' }}
          loading={settingPassword}
          disabled={Object.keys(errors) > 0}
        />
      </form>
      <DefaultButton
        name="Resend Code"
        style={{ width: '100%', marginTop: '20px' }}
        className="light"
        loading={sendingConfirmation}
        onClick={onResendCode}
      />
    </>
  );
};

export default ForgotPassword;
